import { useState, useMemo } from "react";
import {
  Sliders,
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle2,
  XCircle,
  RotateCcw,
  Sparkles,
  ArrowRight,
  FileText,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  EyeOff,
} from "lucide-react";
import { useStudio } from "@/store/StudioContext";
import {
  CONTINUITY_RISK_TYPES,
  RISK_TYPE_CONFIG,
  isObservationFixable,
} from "@/domain/continuity";
import { t } from "@/i18n";
import { toast } from "sonner";

/**
 * Inspector de Continuidad Cinematográfica Interactivo (Fase 2B.3B).
 *
 * Permite al realizador:
 * 1. Inspeccionar observaciones en 4 categorías deterministas.
 * 2. Visualizar evidencia textual y escenas involucradas.
 * 3. Revisar y descartar observaciones con persistencia en el estado.
 * 4. Evaluar propuestas de ajuste de prompt con comparación antes/después y aprobación explícita.
 */
export const ContinuityInspector = ({
  projectId,
  selectedScene,
  scenes = [],
  observations = [],
  decisions = {},
  className = "",
}) => {
  const { dispatch } = useStudio();
  const [filterScope, setFilterScope] = useState("scene"); // 'scene' | 'all'
  const [filterStatus, setFilterStatus] = useState("pending"); // 'pending' | 'decided'
  const [expandedDiffs, setExpandedDiffs] = useState({});
  const [expandedPrompts, setExpandedPrompts] = useState({});
  const [informativeExpanded, setInformativeExpanded] = useState(false);
  const [helpExpanded, setHelpExpanded] = useState(false);

  // Escena seleccionada
  const selectedSceneId = selectedScene?.id;

  // Filtrado de observaciones
  const filteredObservations = useMemo(() => {
    return observations.filter((obs) => {
      // Filtro de alcance
      if (filterScope === "scene" && selectedSceneId) {
        if (!obs.affectedSceneIds?.includes(selectedSceneId)) return false;
      }

      // Filtro de estado
      const decision = decisions[obs.id];
      const isDecided = decision && decision.status !== "pending";

      if (filterStatus === "pending") return !isDecided;
      if (filterStatus === "decided") return isDecided;
      return true;
    });
  }, [observations, decisions, filterScope, filterStatus, selectedSceneId]);

  // Jerarquía visual: observaciones que requieren atención vs notas informativas
  const attentionObservations = useMemo(
    () => filteredObservations.filter((obs) => obs.severity !== "info"),
    [filteredObservations]
  );
  const informativeObservations = useMemo(
    () => filteredObservations.filter((obs) => obs.severity === "info"),
    [filteredObservations]
  );

  // Contadores (únicamente observaciones que requieren atención, excluyendo notas informativas)
  const pendingCount = useMemo(() => {
    return observations.filter((obs) => {
      if (obs.severity === "info") return false;
      const dec = decisions[obs.id];
      return !dec || dec.status === "pending";
    }).length;
  }, [observations, decisions]);

  const scenePendingCount = useMemo(() => {
    if (!selectedSceneId) return 0;
    return observations.filter((obs) => {
      if (obs.severity === "info") return false;
      if (!obs.affectedSceneIds?.includes(selectedSceneId)) return false;
      const dec = decisions[obs.id];
      return !dec || dec.status === "pending";
    }).length;
  }, [observations, decisions, selectedSceneId]);

  // Manejadores de decisiones
  const handleSetDecision = (observationId, status, note = null) => {
    dispatch({
      type: "CONTINUITY_DECISION_SET",
      payload: { projectId, observationId, status, note },
    });
    if (status === "reviewed") {
      toast.success(t.continuity.statusReviewed);
    } else if (status === "dismissed") {
      toast(t.continuity.statusDismissed);
    } else if (status === "pending") {
      toast(t.continuity.restore);
    }
  };

  // Aplicación explícita de propuesta
  const handleApplyProposal = (obs) => {
    if (!obs.proposedFix) return;
    const { sceneId, afterText } = obs.proposedFix;
    dispatch({
      type: "CONTINUITY_PROPOSAL_APPLY",
      payload: {
        projectId,
        observationId: obs.id,
        sceneId,
        newPrompt: afterText,
      },
    });
    toast.success(t.continuity.appliedSuccess);
  };

  const toggleDiff = (id) => {
    setExpandedDiffs((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Helper para nombre de escena
  const getSceneLabel = (scId) => {
    const sc = scenes.find((s) => s.id === scId);
    if (!sc) return scId;
    const order = (sc.order ?? 0) + 1;
    return sc.title ? `Toma #${order}: ${sc.title}` : `Toma #${order}`;
  };

  return (
    <div
      data-testid="continuity-inspector"
      className={`flex flex-col gap-3 rounded-lg border border-[#303030] bg-[#141414] p-3 md:p-4 ${className}`}
    >
      {/* Cabecera del Inspector */}
      <div className="flex items-start justify-between gap-2 border-b border-[#262626] pb-3">
        <div>
          <div className="flex items-center gap-2">
            <Sliders size={15} className="text-[#f5b544]" />
            <h3 className="text-xs font-semibold uppercase tracking-wider text-[#f4f1ec]">
              {t.continuity.title}
            </h3>
            {pendingCount > 0 && (
              <span
                data-testid="continuity-pending-counter"
                className="inline-flex items-center rounded-full bg-[#f5b544]/15 px-2 py-0.5 font-mono text-[10px] font-semibold text-[#f5b544]"
              >
                {pendingCount}
              </span>
            )}
          </div>
          <p className="mt-1 text-[11px] leading-relaxed text-[#8a8a8a]">
            {t.continuity.subtitle}
          </p>
        </div>
      </div>

      {/* Declaración verídica de análisis textual (sin visión artificial) */}
      <div
        data-testid="continuity-analysis-disclaimer"
        className="flex items-center gap-2 rounded border border-[#2a2a2a] bg-[#101010] px-2.5 py-1.5 text-[10px] text-[#8e8e8e]"
      >
        <EyeOff size={13} className="shrink-0 text-[#a3a3a3]" />
        <span>{t.continuity.disclaimer}</span>
      </div>

      {/* Barra de filtros */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
        {/* Alcance: Esta escena vs Todo el proyecto */}
        <div className="inline-flex rounded-md border border-[#2e2e2e] bg-[#101010] p-0.5">
          <button
            type="button"
            data-testid="filter-scope-scene"
            title={t.continuity.filterSceneHint}
            onClick={() => setFilterScope("scene")}
            className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
              filterScope === "scene"
                ? "bg-[#252525] text-[#f4f1ec]"
                : "text-[#8a8a8a] hover:text-[#d4d4d4]"
            }`}
          >
            {t.continuity.filterScene} {selectedScene && `(${scenePendingCount})`}
          </button>
          <button
            type="button"
            data-testid="filter-scope-all"
            title={t.continuity.filterAllHint}
            onClick={() => setFilterScope("all")}
            className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
              filterScope === "all"
                ? "bg-[#252525] text-[#f4f1ec]"
                : "text-[#8a8a8a] hover:text-[#d4d4d4]"
            }`}
          >
            {t.continuity.filterAll} ({observations.length})
          </button>
        </div>

        {/* Estado: Pendientes vs Decididas */}
        <div className="inline-flex rounded-md border border-[#2e2e2e] bg-[#101010] p-0.5">
          <button
            type="button"
            data-testid="filter-status-pending"
            title={t.continuity.filterPendingHint}
            onClick={() => setFilterStatus("pending")}
            className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
              filterStatus === "pending"
                ? "bg-[#252525] text-[#f5b544]"
                : "text-[#8a8a8a] hover:text-[#d4d4d4]"
            }`}
          >
            {t.continuity.filterPending}
          </button>
          <button
            type="button"
            data-testid="filter-status-decided"
            title={t.continuity.filterDecidedHint}
            onClick={() => setFilterStatus("decided")}
            className={`rounded px-2 py-1 text-[11px] font-medium transition-colors ${
              filterStatus === "decided"
                ? "bg-[#252525] text-[#a3a3a3]"
                : "text-[#8a8a8a] hover:text-[#d4d4d4]"
            }`}
          >
            {t.continuity.filterDecided}
          </button>
        </div>
      </div>

      {/* Guía breve de acciones */}
      <div
        data-testid="continuity-help-guide"
        className="rounded border border-[#262626] bg-[#101010] p-2 text-[11px] text-[#a3a3a3]"
      >
        <button
          type="button"
          onClick={() => setHelpExpanded((prev) => !prev)}
          className="flex w-full items-center justify-between font-medium text-[#c4c0b6] hover:text-[#f4f1ec]"
        >
          <span className="flex items-center gap-1.5">
            <Info size={12} className="text-[#f5b544]" />
            <span>{t.continuity.helpTitle}</span>
          </span>
          {helpExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
        </button>
        {helpExpanded && (
          <ul className="mt-2 space-y-1.5 border-t border-[#222] pt-2 text-[10px] leading-relaxed text-[#8e8e8e]">
            <li>
              <span className="font-medium text-[#d4d4d4]">{t.continuity.filterScene} / {t.continuity.filterAll}:</span>{" "}
              {t.continuity.filterSceneHint}
            </li>
            <li>
              <span className="font-medium text-[#d4d4d4]">{t.continuity.filterPending} / {t.continuity.filterDecided}:</span>{" "}
              {t.continuity.filterPendingHint}
            </li>
            <li>
              <span className="font-medium text-[#d4d4d4]">{t.continuity.markReviewed}:</span>{" "}
              {t.continuity.markReviewedHint}
            </li>
            <li>
              <span className="font-medium text-[#d4d4d4]">{t.continuity.dismiss}:</span>{" "}
              {t.continuity.dismissHint}
            </li>
            <li>
              <span className="font-medium text-[#d4d4d4]">{t.continuity.restoreTitle}:</span>{" "}
              {t.continuity.restoreHint}
            </li>
            <li>
              <span className="font-medium text-[#d4d4d4]">{t.continuity.applyProposalTitle}:</span>{" "}
              {t.continuity.applyProposalHint}
            </li>
          </ul>
        )}
      </div>

      {/* Función para renderizar tarjeta de observación individual */}
      {(() => {
        const renderObservationCard = (obs) => {
          const config =
            RISK_TYPE_CONFIG[obs.riskType] ||
            RISK_TYPE_CONFIG[CONTINUITY_RISK_TYPES.MISSING_INFORMATION];
          const decision = decisions[obs.id];
          const isReviewed = decision?.status === "reviewed";
          const isDismissed = decision?.status === "dismissed";
          const isApplied = decision?.status === "applied";
          const diffOpen = expandedDiffs[obs.id] ?? Boolean(obs.proposedFix);

          const isUnlinked = Boolean(obs.isUnlinkedRef);
          const isSingleScene = obs.id?.includes("single_scene_scope");
          const badgeLabel = isUnlinked
            ? t.continuity.possibleUnlinkedRef
            : isSingleScene
            ? (t.continuity.analysisScope || "Alcance del análisis")
            : config.label;
          const badgeClass = isUnlinked
            ? "border-amber-500/50 bg-amber-500/15 text-amber-300"
            : isSingleScene
            ? "border-zinc-500/40 bg-zinc-500/10 text-zinc-300"
            : config.badgeClass;

          const CategoryIcon =
            obs.riskType === CONTINUITY_RISK_TYPES.REAL_CONTRADICTION
              ? AlertCircle
              : obs.riskType === CONTINUITY_RISK_TYPES.POSSIBLE_RISK || isUnlinked
              ? AlertTriangle
              : Info;

          const cardBorderBg =
            decision && decision.status !== "pending"
              ? "border-[#282828] bg-[#121212] opacity-80"
              : isUnlinked
              ? "border-amber-500/35 bg-[#18150f]"
              : "border-[#333] bg-[#181818]";

          return (
            <div
              key={obs.id}
              data-testid={`continuity-observation-card-${obs.id}`}
              className={`flex flex-col gap-2 rounded-md border p-3 transition-colors ${cardBorderBg}`}
            >
              {/* Categoría y Severidad */}
              <div className="flex flex-wrap items-center justify-between gap-1.5">
                <span
                  data-testid={`continuity-risk-badge-${obs.id}`}
                  className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold ${badgeClass}`}
                >
                  <CategoryIcon size={11} className="shrink-0" />
                  <span>{badgeLabel}</span>
                </span>

                {/* Estado de decisión previo si existe */}
                {decision && decision.status !== "pending" && (
                  <span
                    data-testid={`continuity-decision-badge-${obs.id}`}
                    className="inline-flex items-center gap-1 rounded bg-[#222] px-2 py-0.5 font-mono text-[10px] text-[#a3a3a3]"
                  >
                    {isApplied && <CheckCircle2 size={11} className="text-emerald-400" />}
                    {isReviewed && <CheckCircle2 size={11} className="text-emerald-400" />}
                    {isDismissed && <XCircle size={11} className="text-zinc-500" />}
                    <span>
                      {isApplied
                        ? t.continuity.statusApplied
                        : isReviewed
                        ? t.continuity.statusReviewed
                        : t.continuity.statusDismissed}
                    </span>
                  </span>
                )}
              </div>

              {/* Título y Descripción */}
              <div>
                <h4 className="text-xs font-medium text-[#f4f1ec]">{obs.title}</h4>
                <p className="mt-1 text-[11px] leading-relaxed text-[#b5b0a7]">
                  {obs.description}
                </p>
              </div>

              {/* Escenas Afectadas */}
              {obs.affectedSceneIds && obs.affectedSceneIds.length > 0 && (
                <div className="flex flex-wrap items-center gap-1 text-[10px] text-[#8a8a8a]">
                  <span className="font-mono text-[#666]">{t.continuity.affectedScenes}:</span>
                  {obs.affectedSceneIds.map((scId) => (
                    <span
                      key={scId}
                      className={`rounded border px-1.5 py-0.5 font-mono ${
                        scId === selectedSceneId
                          ? "border-[#f5b544]/60 bg-[#f5b544]/10 text-[#f5b544]"
                          : "border-[#333] bg-[#1a1a1a] text-[#a3a3a3]"
                      }`}
                    >
                      {getSceneLabel(scId)}
                    </span>
                  ))}
                </div>
              )}

              {/* Evidencia textual disponible */}
              {obs.evidence && Object.keys(obs.evidence).length > 0 && (
                <div
                  data-testid={`continuity-evidence-${obs.id}`}
                  className="rounded border border-[#262626] bg-[#101010] p-2 text-[10px]"
                >
                  <div className="font-mono text-[#666] mb-1">{t.continuity.evidence}:</div>
                  {obs.evidence.canonText && (
                    <div className="text-[#a3a3a3]">
                      <span className="text-[#f5b544]/80">Canon:</span> "{obs.evidence.canonText}"
                    </div>
                  )}
                  {obs.evidence.prevSceneText && (
                    <div className="text-[#a3a3a3]">
                      <span className="text-sky-400/80">Toma anterior:</span> "{obs.evidence.prevSceneText}"
                    </div>
                  )}
                  {obs.evidence.sceneText && (
                    <div className="text-[#a3a3a3]">
                      <span className={obs.severity === "conflict" ? "text-red-400/80 font-medium" : "text-[#888]"}>
                        Toma actual:
                      </span>{" "}
                      {isSingleScene ? (
                        <div className="mt-0.5">
                          <span className="text-[#ccc]">
                            {t.continuity?.singleSceneReviewedBrief || "Texto revisado sin inconsistencias internas."}
                          </span>{" "}
                          <button
                            type="button"
                            onClick={() => setExpandedPrompts((prev) => ({ ...prev, [obs.id]: !prev[obs.id] }))}
                            className="text-[#f5b544] hover:underline cursor-pointer text-[10px] inline-block ml-1"
                          >
                            {expandedPrompts[obs.id]
                              ? (t.continuity?.hideFullPrompt || "Ocultar texto")
                              : (t.continuity?.viewFullPrompt || "Ver texto completo")}
                          </button>
                          {expandedPrompts[obs.id] && (
                            <div className="mt-1 rounded bg-[#161616] border border-[#2a2a2a] p-1.5 text-[#bbb] whitespace-pre-wrap max-h-36 overflow-y-auto">
                              "{obs.evidence.sceneText}"
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className={obs.severity === "conflict" ? "text-red-400/90 font-medium" : "text-[#ccc]"}>
                          "{obs.evidence.sceneText}"
                        </span>
                      )}
                    </div>
                  )}
                  {obs.evidence.targetSceneText && !obs.evidence.sceneText && (
                    <div className="text-[#a3a3a3]">
                      <span className={obs.severity === "conflict" ? "text-red-400/80 font-medium" : "text-[#888]"}>
                        Toma actual:
                      </span>{" "}
                      <span className={obs.severity === "conflict" ? "text-red-400/90 font-medium" : "text-[#ccc]"}>
                        "{obs.evidence.targetSceneText}"
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Recomendación consultiva */}
              {obs.recommendation && (
                <div className="text-[11px] leading-relaxed text-[#c2beb4]">
                  <span className="font-semibold text-[#8a8a8a]">{t.continuity.recommendation}: </span>
                  {obs.recommendation}
                </div>
              )}

              {/* Propuesta de modificación y Diff Antes / Después */}
              {obs.proposedFix && (
                <div
                  data-testid={`continuity-proposal-${obs.id}`}
                  className="mt-1 rounded border border-[#303030] bg-[#121212] p-2.5"
                >
                  <div
                    className="flex cursor-pointer items-center justify-between gap-2 text-xs font-medium text-[#f5b544]"
                    onClick={() => toggleDiff(obs.id)}
                  >
                    <div className="flex items-center gap-1.5">
                      <Sparkles size={12} />
                      <span>{t.continuity.proposedChange}</span>
                    </div>
                    <button
                      type="button"
                      aria-label="Toggle diff"
                      className="text-[#a3a3a3] hover:text-[#f4f1ec]"
                    >
                      {diffOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                  </div>

                  {diffOpen && (
                    <div className="mt-2 flex flex-col gap-2">
                      {obs.proposedFix.explanation && (
                        <p className="text-[10px] text-[#8a8a8a] italic">
                          {obs.proposedFix.explanation}
                        </p>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[10px] font-mono">
                        {/* Antes */}
                        <div className="rounded border border-red-500/20 bg-red-950/15 p-2 text-red-200">
                          <div className="text-[9px] font-bold uppercase tracking-wider text-red-400 mb-1">
                            {t.continuity.beforeText}
                          </div>
                          <div className="line-clamp-4 leading-relaxed whitespace-pre-wrap">
                            {obs.proposedFix.beforeText}
                          </div>
                        </div>

                        {/* Después */}
                        <div className="rounded border border-emerald-500/20 bg-emerald-950/15 p-2 text-emerald-200">
                          <div className="text-[9px] font-bold uppercase tracking-wider text-emerald-400 mb-1">
                            {t.continuity.afterText}
                          </div>
                          <div className="line-clamp-4 leading-relaxed whitespace-pre-wrap">
                            {obs.proposedFix.afterText}
                          </div>
                        </div>
                      </div>

                      {/* Botón de aplicación con confirmación explícita (NO automático) */}
                      <div className="mt-1 flex justify-end">
                        <button
                          type="button"
                          data-testid={`continuity-apply-btn-${obs.id}`}
                          title={t.continuity.applyProposalHint}
                          onClick={() => handleApplyProposal(obs)}
                          disabled={isApplied}
                          className="v-btn-primary v-btn-sm text-[11px]"
                        >
                          <Sparkles size={12} />
                          <span>
                            {isApplied ? t.continuity.statusApplied : t.continuity.applyProposal}
                          </span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Botones de acción / decisión del usuario */}
              <div className="mt-1 flex flex-wrap items-center justify-end gap-1.5 pt-2 border-t border-[#222]">
                {decision && decision.status !== "pending" ? (
                  <button
                    type="button"
                    data-testid={`continuity-restore-btn-${obs.id}`}
                    title={t.continuity.restoreHint}
                    onClick={() => handleSetDecision(obs.id, "pending")}
                    className="v-btn-ghost v-btn-sm text-[11px] text-[#a3a3a3] hover:text-[#f4f1ec]"
                  >
                    <RotateCcw size={12} />
                    <span>{t.continuity.restore}</span>
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      data-testid={`continuity-dismiss-btn-${obs.id}`}
                      title={t.continuity.dismissHint}
                      onClick={() => handleSetDecision(obs.id, "dismissed")}
                      className="v-btn-ghost v-btn-sm text-[11px] text-[#8a8a8a] hover:text-[#e5655a]"
                    >
                      <XCircle size={12} />
                      <span>{t.continuity.dismiss}</span>
                    </button>
                    <button
                      type="button"
                      data-testid={`continuity-review-btn-${obs.id}`}
                      title={t.continuity.markReviewedHint}
                      onClick={() => handleSetDecision(obs.id, "reviewed")}
                      className="v-btn-ghost v-btn-sm text-[11px] text-[#f5b544] hover:bg-[#f5b544]/10"
                    >
                      <CheckCircle2 size={12} />
                      <span>{t.continuity.markReviewed}</span>
                    </button>
                    {isObservationFixable(obs) && (
                      <button
                        type="button"
                        data-testid={`continuity-request-fix-btn-${obs.id}`}
                        title={t.continuity.requestFixHint || "Prepara una corrección en el chat del Asistente"}
                        onClick={() => {
                          window.dispatchEvent(
                            new CustomEvent("veyra:continuity:request-fix", {
                              detail: { observation: obs, sceneId: selectedSceneId || obs.affectedSceneIds?.[0] },
                            })
                          );
                        }}
                        className="v-btn-ghost v-btn-sm text-[11px] text-[#f5b544] hover:bg-[#201c13]"
                      >
                        <Sparkles size={12} />
                        <span>{t.continuity.requestFix || "Solicitar corrección"}</span>
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          );
        };

        return (
          <div className="flex flex-col gap-3">
            {filteredObservations.length === 0 ? (
              <div
                data-testid="continuity-empty-state"
                className="flex flex-col items-center justify-center rounded border border-dashed border-[#2b2b2b] bg-[#101010]/60 p-5 text-center"
              >
                <ShieldCheck size={24} className="text-[#555] mb-1.5" />
                <p className="text-xs text-[#a3a3a3]">
                  {filterScope === "scene"
                    ? t.continuity.noSceneObservations
                    : t.continuity.noObservations}
                </p>
              </div>
            ) : (
              <>
                {/* 1. Observaciones que requieren atención (contradicciones reales, riesgos y referencias sin vincular) */}
                {attentionObservations.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center justify-between text-xs font-semibold text-[#f5b544] px-0.5 pt-0.5">
                      <span className="flex items-center gap-1.5">
                        <AlertTriangle size={13} />
                        <span>{t.continuity.attentionTitle}</span>
                        <span className="rounded-full bg-[#f5b544]/20 px-1.5 py-0.2 font-mono text-[10px] text-[#f5b544]">
                          {attentionObservations.length}
                        </span>
                      </span>
                    </div>
                    <div className="flex flex-col gap-2.5">
                      {attentionObservations.map(renderObservationCard)}
                    </div>
                  </div>
                ) : (
                  <div
                    data-testid="continuity-clean-state"
                    className="flex items-start gap-2.5 rounded-lg border border-[#059669]/30 bg-[#064e3b]/15 p-3 text-xs text-[#34d399]"
                  >
                    <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-[#34d399]" />
                    <div>
                      <div className="font-semibold text-[#a7f3d0]">
                        {t.continuity.cleanState || "No se detectaron contradicciones en la información revisada"}
                      </div>
                      <div className="text-[11px] text-[#a7f3d0]/80 mt-0.5">
                        {t.continuity.cleanStateDesc || "El análisis revisa texto y metadatos; esto no garantiza continuidad visual de los fotogramas generados."}
                      </div>
                    </div>
                  </div>
                )}

                {/* 2. Información de reparto y notas consultivas (agrupable y contraíble para evitar saturación) */}
                {informativeObservations.length > 0 && (
                  <div className="flex flex-col gap-2 mt-1">
                    <button
                      type="button"
                      data-testid="continuity-informative-toggle"
                      onClick={() => setInformativeExpanded((e) => !e)}
                      className="flex w-full items-center justify-between rounded-md border border-[#2a2a2a] bg-[#121212] px-3 py-2 text-xs font-medium text-[#a3a3a3] hover:text-[#f4f1ec] hover:border-[#383838] transition-colors"
                    >
                      <span className="flex items-center gap-1.5">
                        <Info size={13} className="text-[#888]" />
                        <span>{t.continuity.informativeTitle}</span>
                        <span className="rounded-full bg-[#202020] px-1.5 py-0.2 font-mono text-[10px] text-[#888]">
                          {informativeObservations.length}
                        </span>
                      </span>
                      {informativeExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                    </button>
                    {informativeExpanded && (
                      <div className="flex flex-col gap-2.5">
                        {informativeObservations.map(renderObservationCard)}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}
    </div>
  );
};

export default ContinuityInspector;
